import React from 'react';
import {
  Flex,
  useColorMode,
  useIsMobileView,
  Text,
  typography,
  color,
  StashedSvg,
} from 'deepstash-ui';
import { StoryApiResponse } from 'api/api.types';
import ReactHtmlParser from 'react-html-parser';
import { Analytics, Events } from 'services/analytics';
import { useRouter } from 'next/router';
import useAuthAndOpenPurchasePage from 'hooks/purchases/useAuthAndOpenPurchasePage';

const TITLE = 'How a key idea looks within Deepstash';

interface StoryIdeaCardProps {
  story: StoryApiResponse;
}

const StoryIdeaCard: React.FC<StoryIdeaCardProps> = ({ story }) => {
  const isMobileView = useIsMobileView();
  const { colorMode } = useColorMode();
  const router = useRouter();

  const { onClickStartSubscriptionForNsi } = useAuthAndOpenPurchasePage({});

  const ideaParsed = React.useMemo(() => {
    return ReactHtmlParser(story.featured_idea);
  }, [story.featured_idea]);

  const onClickSaveIdea = () => {
    Analytics.logEvent({
      eventName: Events.story.clickSaveIdea,
      properties: {
        url: router.asPath,
      },
      platforms: ['amplitude'],
    });

    onClickStartSubscriptionForNsi();
  };

  return (
    <Flex width={'100%'} {...(isMobileView ? {} : { pl: 24 })}>
      {/** Divider */}
      {isMobileView === false && (
        <Flex
          width={2}
          height={'100%'}
          bgColor={color[colorMode].underground}
        />
      )}
      <Flex
        width={'100%'}
        flexDirection="column"
        alignItems="center"
        px={isMobileView ? 0 : 24}
        py={24}
      >
        <Text
          {...typography.primaryTextFonts.large.regular}
          textAlign="center"
          color={color[colorMode].textSecondary}
        >
          {TITLE}
        </Text>
        <Flex
          width={'100%'}
          bgColor={color[colorMode].top}
          px={24}
          pb={24}
          borderRadius={24}
          flexDirection="column"
          mt={16}
          // boxShadow="0px 6px 12px rgba(0, 0, 0, 0.08); 0px 8px 4px rgba(0, 0, 0, 0.05);"
        >
          <Flex width={'100%'}>
            <Text
              {...typography.primaryTextFonts.medium.regular}
              color={color[colorMode].text}
            >
              {ideaParsed}
            </Text>
          </Flex>
          <Flex width={'100%'} mt={8} flexDirection="row" alignItems="center">
            <StashedSvg
              fill={color[colorMode].textSecondary}
              _hover={{
                fill: color[colorMode].text,
                bgColor: color[colorMode].underground,
                cursor: 'pointer',
              }}
              p={6}
              boxSize={'36px'}
              borderRadius={8}
              onClick={onClickSaveIdea}
            />

            <Text
              {...typography.primaryTextFonts.small.regular}
              color={color[colorMode].textSecondary}
              ml={2}
            >
              11k people saved this idea
            </Text>
          </Flex>
        </Flex>
        <Text
          {...typography.primaryTextFonts.small.regular}
          color={color[colorMode].textSecondary}
          mt={16}
        >
          {
            'The idea format used by Deepstash is important because it condenses complex concepts and profound thoughts into easy to understand sentences. It makes ideas easier to apply in daily life, helping you to make positive changes and achieve your goals.'
          }
        </Text>
      </Flex>
    </Flex>
  );
};

export default StoryIdeaCard;
